// Helpers for logging HTTP responses without leaking secrets.
//
// The auth flow exchanges short-lived but replay-attackable values (Auth0
// codes, tokens, refresh tokens). When we log responses for diagnostics we
// MUST redact those values; otherwise log shipping (logcat, Sentry, error
// reporters) would persist them.

// Field names whose VALUES are sensitive. Logged as `<redacted len=N>` so we
// keep visibility of shape and presence without leaking the value.
const SENSITIVE_FIELDS = new Set([
  "access_token",
  "refresh_token",
  "id_token",
  "code",
  "authorization_code",
  "state",
  "token",
]);

export const redactSensitiveValues = (obj: unknown): unknown => {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(redactSensitiveValues);
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    if (SENSITIVE_FIELDS.has(key) && typeof value === "string") {
      out[key] = `<redacted len=${value.length}>`;
    } else if (typeof value === "object" && value !== null) {
      out[key] = redactSensitiveValues(value);
    } else {
      out[key] = value;
    }
  }
  return out;
};

// Reads response.text() once and returns:
// - `text`: the FULL response body, suitable for downstream JSON.parse
// - `logText`: truncated to 2000 chars for log display (avoids huge logcat
//   dumps on chatty backends)
// - `getRedactedParsed()`: lazy best-effort JSON parse + recursive redaction
//   of the full text. Memoized. Skipped entirely on the success path where
//   neither the auth log nor any Sentry error capture fires — important
//   because the redact walk is O(body size) and runs on every auth request.
//
// Safe to call once per Response — the body is consumed.
export const readResponseForLogging = async (
  response: Response,
): Promise<{
  text: string;
  logText: string;
  getRedactedParsed: () => unknown;
}> => {
  try {
    const text = await response.text();
    const logText = text.length > 2000 ? `${text.slice(0, 2000)}…` : text;
    let cached: { value: unknown } | null = null;
    const getRedactedParsed = () => {
      if (cached) return cached.value;
      try {
        cached = { value: redactSensitiveValues(JSON.parse(text)) };
      } catch {
        cached = { value: null };
      }
      return cached.value;
    };
    return { text, logText, getRedactedParsed };
  } catch (e) {
    const errText = `<read-failed: ${(e as Error)?.message ?? "unknown"}>`;
    return { text: errText, logText: errText, getRedactedParsed: () => null };
  }
};

// Common diagnostic headers worth surfacing in logs. Intentionally excludes
// cookies and authorization headers — those would either not be present on
// responses or shouldn't be echoed to logs.
const DIAGNOSTIC_HEADER_KEYS = [
  "content-type",
  "content-length",
  "x-amzn-trace-id",
  "x-amz-cf-id",
  "x-request-id",
  "x-correlation-id",
  "server",
  "date",
] as const;

// Extract diagnostic headers from either a Fetch Response or an axios-style
// object exposing `headers` as a plain record. Returns lowercased keys.
export const extractDiagnosticHeaders = (
  source: Response | { headers: Record<string, unknown> },
): Record<string, string> => {
  const out: Record<string, string> = {};
  if (source instanceof Response) {
    for (const key of DIAGNOSTIC_HEADER_KEYS) {
      const value = source.headers.get(key);
      if (value) out[key] = value;
    }
    return out;
  }
  const headers = source?.headers ?? {};
  for (const key of DIAGNOSTIC_HEADER_KEYS) {
    const value = headers[key];
    if (typeof value === "string") out[key] = value;
  }
  return out;
};
